import AffectationStatusBadge from '@/components/Affectations/AffectationStatusBadge';
import { useDeleteAffectationMutation } from '@/features/api/endpoints/affectationEndpoints';
import type { Affectation } from '@/models/types';
import { DATE_TIME_FORMAT } from '@/shared/utils/date-utils';
import { ActionIcon, Group, Text, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconTrash } from '@tabler/icons-react';
import { useTranslate } from '@tolgee/react';
import dayjs from 'dayjs';
import isObject from 'lodash/isObject';
import { DataTable } from 'mantine-datatable';

type Props = {
  affectations: Affectation[];
};

const AffectationsTable = ({ affectations }: Props) => {
  const { t } = useTranslate();
  // const dispatch = useAppDispatch();

  const [deleteAffectation] = useDeleteAffectationMutation();

  const publishedCount = affectations.filter((a: Affectation) => a.status === 'published').length;

  const isLastPublished = (a: Affectation) => a.status === 'published' && publishedCount === 1;

  const onDeleteClick = (id: number) => {
    modals.openConfirmModal({
      title: t('profil.affectations.delete.confirm'),
      children: <Text size="sm">{t('profil.affectations.delete.message')}</Text>,
      labels: {
        confirm: t('profil.affectations.delete'),
        cancel: t('common.cancel'),
      },
      confirmProps: { color: 'red' },
      centered: true,
      onConfirm: async () => {
        try {
          await deleteAffectation(id).unwrap();
          notifications.show({
            color: 'green',
            message: t('profil.affectations.delete.success'),
          });
        } catch {
          notifications.show({
            color: 'red',
            message: t('profil.affectations.form.error.submit'),
          });
        }
      },
    });
  };

  return (
    <>
      <DataTable
        striped
        highlightOnHover
        withTableBorder
        minHeight={120}
        records={affectations}
        columns={[
          {
            accessor: 'date_created',
            title: t('profil.affectations.columns.date_created'),
            textAlign: 'center',
            render: (a: Affectation) =>
              a.date_created ? dayjs(a.date_created).format(DATE_TIME_FORMAT) : '-',
          },
          {
            accessor: 'role',
            title: t('profil.affectations.columns.role'),
            render: (a: Affectation) => {
              const role = isObject(a.role) ? a.role : undefined;
              return role ? role.name : '-';
            },
          },
          {
            accessor: 'etablissement',
            title: t('profil.affectations.columns.etablissement'),
            render: (a: Affectation) => {
              const etablissement = isObject(a.etablissement) ? a.etablissement : undefined;
              return etablissement ? etablissement.name : '-';
            },
          },
          {
            accessor: 'status',
            title: t('profil.affectations.columns.status'),
            render: (a: Affectation) => <AffectationStatusBadge status={a.status} />,
          },
          {
            accessor: 'actions',
            title: t('profil.affectations.columns.actions'),
            render: (a: Affectation) => {
              if (a.status === 'archived') {
                return null;
              }

              const lastPub = isLastPublished(a);
              return (
                <Group gap="xs" wrap="nowrap">
                  <Tooltip
                    label={t('profil.affectations.delete.lastPublished')}
                    disabled={!lastPub}
                    withArrow
                  >
                    <span>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        disabled={lastPub}
                        onClick={() => onDeleteClick(a.id)}
                        aria-label={t('profil.affectations.delete')}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </span>
                  </Tooltip>
                </Group>
              );
            },
          },
        ]}
      />
    </>
  );
};

export default AffectationsTable;
